import { useEffect } from "react";
import { SITE } from "@/data/site";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
}

export function useSeo({ title, description, path = "/", image, jsonLd } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE.name}` : `${SITE.name} — Pool Cleaning & Service in Tampa Bay, FL`;
    document.title = fullTitle;

    const canonical = `${SITE.canonicalDomain}${path}`;
    // Always send an absolute URL for og:image
    const ogImage = (() => {
      const candidate = image || `${SITE.canonicalDomain}/og-card.png`;
      if (candidate.startsWith("http")) return candidate;
      return `${SITE.canonicalDomain}${candidate.startsWith("/") ? "" : "/"}${candidate}`;
    })();
    setMeta("description", description);
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("og:site_name", SITE.name, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    setLink("canonical", canonical);

    if (jsonLd) {
      setJsonLd("page-jsonld", jsonLd);
    }
  }, [title, description, path, image, jsonLd]);
}

export function localBusinessJsonLd(extra = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE.canonicalDomain}/#business`,
    name: SITE.name,
    image: SITE.logo,
    logo: SITE.logo,
    url: SITE.canonicalDomain,
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "$$",
    foundingDate: SITE.established,
    sameAs: [SITE.facebook, SITE.legacyDomain],
    areaServed: [
      "Clearwater, FL",
      "Tarpon Springs, FL",
      "Trinity, FL",
      "Dunedin, FL",
      "Palm Harbor, FL",
      "Oldsmar, FL",
      "Westchase, FL",
      "Town and Country, FL",
      "Odessa, FL",
      "Carrollwood, FL",
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Tampa Bay",
      addressRegion: "FL",
      addressCountry: "US",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
    ...extra,
  };
}
